/**
 * ConfirmDialog - Styled confirmation dialog to replace browser confirm()
 *
 * Provides a modal dialog for confirmation actions with customizable
 * title, message, and button labels.
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AlertTriangle, Info, HelpCircle, Trash2, X } from 'lucide-react';

export type ConfirmVariant = 'danger' | 'warning' | 'info' | 'default';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  /** Optional input field for extra confirmation (e.g., type "SLETT") */
  requireInput?: string;
  inputPlaceholder?: string;
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
}

const VARIANT_ICONS = {
  danger: Trash2,
  warning: AlertTriangle,
  info: Info,
  default: HelpCircle
};

const VARIANT_STYLES = {
  danger: {
    icon: 'text-red-500 bg-red-100',
    button: 'bg-red-600 hover:bg-red-700 text-white'
  },
  warning: {
    icon: 'text-yellow-600 bg-yellow-100',
    button: 'bg-yellow-600 hover:bg-yellow-700 text-white'
  },
  info: {
    icon: 'text-kf-blue-500 bg-kf-blue-100',
    button: 'bg-kf-blue-600 hover:bg-kf-blue-700 text-white'
  },
  default: {
    icon: 'text-gray-500 bg-gray-100',
    button: 'bg-kf-primary hover:bg-kf-secondary text-white'
  }
};

interface DialogState {
  isOpen: boolean;
  options: ConfirmOptions;
  resolve: ((value: boolean) => void) | null;
}

interface ConfirmProviderProps {
  children: ReactNode;
}

export function ConfirmProvider({ children }: ConfirmProviderProps) {
  const [dialog, setDialog] = useState<DialogState>({
    isOpen: false,
    options: { message: '' },
    resolve: null
  });
  const [inputValue, setInputValue] = useState('');

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setInputValue('');
      setDialog({
        isOpen: true,
        options,
        resolve
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (dialog.options.requireInput && inputValue !== dialog.options.requireInput) {
      return; // Don't close if input doesn't match
    }
    dialog.resolve?.(true);
    setDialog(prev => ({ ...prev, isOpen: false, resolve: null }));
  }, [dialog, inputValue]);

  const handleCancel = useCallback(() => {
    dialog.resolve?.(false);
    setDialog(prev => ({ ...prev, isOpen: false, resolve: null }));
  }, [dialog]);

  const variant = dialog.options.variant || 'default';
  const Icon = VARIANT_ICONS[variant];
  const styles = VARIANT_STYLES[variant];

  const inputRequired = dialog.options.requireInput;
  const inputMatches = !inputRequired || inputValue === inputRequired;

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      {dialog.isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancel();
          }}
        >
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${styles.icon}`}>
                  <Icon size={20} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {dialog.options.title || 'Bekreft handling'}
                </h3>
              </div>
              <button
                onClick={handleCancel}
                className="p-1 hover:bg-gray-100 rounded-full transition"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <p className="text-gray-700 whitespace-pre-wrap">
                {dialog.options.message}
              </p>

              {inputRequired && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Skriv "{inputRequired}" for å bekrefte:
                  </label>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={dialog.options.inputPlaceholder || inputRequired}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      inputMatches
                        ? 'border-green-300 focus:ring-green-500'
                        : 'border-gray-300 focus:ring-kf-primary'
                    }`}
                    autoFocus
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                {dialog.options.cancelLabel || 'Avbryt'}
              </button>
              <button
                onClick={handleConfirm}
                disabled={!inputMatches}
                className={`px-4 py-2 rounded-lg transition ${
                  inputMatches
                    ? styles.button
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {dialog.options.confirmLabel || 'Bekreft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
